import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { MapContainer, TileLayer, Marker } from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

const API_BASE = 'https://locrawl-api.findrawdogfood.workers.dev'

// Fix for default markers in React Leaflet
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
})

export default function CityPage() {
  const { slug } = useParams()
  const [city, setCity] = useState(null)
  const [experiences, setExperiences] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [activeFilter, setActiveFilter] = useState('all')

  useEffect(() => {
    const fetchCityData = async () => {
      try {
        setLoading(true)
        
        // First get all cities to find the one matching the slug
        const citiesResponse = await fetch(`${API_BASE}/api/cities`)
        if (!citiesResponse.ok) {
          throw new Error('Failed to fetch cities')
        }
        
        const citiesData = await citiesResponse.json()
        const foundCity = citiesData.cities.find(c => 
          c.name.toLowerCase().replace(/\s+/g, '-') === slug
        )
        
        if (!foundCity) {
          throw new Error('City not found')
        }
        
        setCity(foundCity)
        
        // Then get experiences for this city
        const expResponse = await fetch(`${API_BASE}/api/experiences?city_id=${foundCity.id}`)
        if (!expResponse.ok) {
          throw new Error('Failed to fetch experiences')
        }
        
        const expData = await expResponse.json()
        setExperiences(expData.experiences || [])
        setError(null)
        
      } catch (err) {
        console.error('Error fetching city data:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    if (slug) {
      fetchCityData()
    }
  }, [slug])

  const filteredExperiences = activeFilter === 'all' 
    ? experiences 
    : experiences.filter(exp => exp.tags && exp.tags.includes(activeFilter))

  const availableTags = [...new Set(experiences.flatMap(exp => exp.tags || []))]

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading city data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">City Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link 
            to="/explore"
            className="bg-black text-white px-6 py-3 rounded-lg hover:bg-gray-800 transition-colors"
          >
            Explore All Cities
          </Link>
        </div>
      </div>
    )
  }

  if (!city) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <Link to="/explore" className="text-blue-600 hover:text-blue-800 text-sm mb-2 inline-block">
                ← Back to Explore
              </Link>
              <h1 className="text-4xl font-bold text-gray-900">{city.name}</h1>
              <p className="text-gray-600">{city.country_name} • {experiences.length} experiences</p>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-500">
                Status: <span className={`font-medium ${
                  city.crawl_status === 'completed' ? 'text-green-600' :
                  city.crawl_status === 'pending' ? 'text-yellow-600' :
                  'text-gray-600'
                }`}>
                  {city.crawl_status}
                </span>
              </div>
              {city.population && (
                <div className="text-sm text-gray-500">
                  Population: {city.population.toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Experiences List */}
          <div className="lg:col-span-2">
            {/* Filter buttons */}
            {availableTags.length > 0 && (
              <div className="mb-6">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setActiveFilter('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      activeFilter === 'all'
                        ? 'bg-black text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    All ({experiences.length})
                  </button>
                  {availableTags.map(tag => (
                    <button
                      key={tag}
                      onClick={() => setActiveFilter(tag)}
                      className={`px-4 py-2 rounded-full text-sm font-medium ${
                        activeFilter === tag
                          ? 'bg-black text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {tag} ({experiences.filter(exp => exp.tags && exp.tags.includes(tag)).length})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Experiences grid */}
            {filteredExperiences.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No experiences found for this filter.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredExperiences.map((experience) => (
                  <div key={experience.id} className="bg-white rounded-lg shadow-sm border p-6 hover:shadow-md transition-shadow">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">
                      {experience.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {experience.description}
                    </p>

                    {experience.tags && experience.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {experience.tags.map((tag, index) => (
                          <span 
                            key={index}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex justify-between items-center text-sm text-gray-500">
                      {experience.best_time && (
                        <span>⏰ Best time: {experience.best_time}</span>
                      )}
                      <span>📍 {experience.lat?.toFixed(4)}, {experience.lon?.toFixed(4)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Map sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
                <div className="h-80">
                  <Map
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={{
                      latitude: city.lat,
                      longitude: city.lon,
                      zoom: 12
                    }}
                    style={{ width: '100%', height: '100%' }}
                    mapStyle="mapbox://styles/mapbox/streets-v12"
                  >
                    {/* City center marker */}
                    <Marker latitude={city.lat} longitude={city.lon}>
                      <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">
                        C
                      </div>
                    </Marker>

                    {/* Experience markers */}
                    {filteredExperiences.map((experience) => (
                      experience.lat && experience.lon && (
                        <Marker
                          key={experience.id}
                          latitude={experience.lat}
                          longitude={experience.lon}
                        >
                          <div className="bg-black text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                            •
                          </div>
                        </Marker>
                      )
                    ))}
                  </Map>
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    {city.name} Overview
                  </h4>
                  <p className="text-sm text-gray-600">
                    Showing {filteredExperiences.length} experiences
                    {activeFilter !== 'all' && ` tagged with "${activeFilter}"`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}